import { Box, Checkbox, Chip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { SELECTED_CHIP_SX } from '../shared';

/** How many of the tickable datasets in view are ticked. */
export type TickState = 'none' | 'some' | 'all';

type DatasetToolbarProps = {
    /** Datasets per modality in the current view, before the filter. */
    composition: ReadonlyArray<{ modality: string; count: number }>;
    /** Modalities the list is narrowed to; empty shows every modality. */
    modalityFilter: ReadonlySet<string>;
    onToggleModality: (modality: string) => void;
    /** Datasets the list shows after the filter, and studies in view. */
    shownCount: number;
    totalCount: number;
    studyCount: number;
    /** Undefined when nothing in view can be ticked. */
    tickState: TickState | undefined;
    onTickAll: () => void;
};

/**
 * The bar over the dataset list: tick every dataset in view, narrow the list
 * to chosen modalities, and say how much it shows.
 */
export default function DatasetToolbar({
    composition,
    modalityFilter,
    onToggleModality,
    shownCount,
    totalCount,
    studyCount,
    tickState,
    onTickAll,
}: DatasetToolbarProps) {
    const intl = useIntl();
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 3,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                minWidth: 0,
            }}
        >
            {tickState !== undefined && (
                <Checkbox
                    checked={tickState === 'all'}
                    indeterminate={tickState === 'some'}
                    onChange={onTickAll}
                    disableRipple
                    inputProps={{
                        'aria-label': intl.formatMessage({
                            id:
                                tickState === 'all'
                                    ? 'patientList.datasets.untickAll'
                                    : 'patientList.datasets.tickAll',
                        }),
                    }}
                    sx={{ p: 0, width: 18, flexShrink: 0 }}
                />
            )}
            <Box
                role="group"
                aria-label={intl.formatMessage({
                    id: 'patientList.datasets.modalityFilter',
                })}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.75,
                    ml: 0.5,
                }}
            >
                {composition.map(({ modality, count }) => {
                    const active = modalityFilter.has(modality);
                    return (
                        <Chip
                            key={modality}
                            size="small"
                            label={`${modality} ×${count}`}
                            variant="outlined"
                            aria-pressed={active}
                            onClick={() => onToggleModality(modality)}
                            sx={{
                                fontFamily: 'fontFamilyMonospace',
                                ...(active ? SELECTED_CHIP_SX : {}),
                            }}
                        />
                    );
                })}
            </Box>
            <Typography
                variant="monoMeta"
                sx={{ fontSize: 13, flexShrink: 0, whiteSpace: 'nowrap' }}
            >
                {shownCount === totalCount
                    ? intl.formatMessage(
                          { id: 'patientList.datasets.count' },
                          { count: totalCount, studies: studyCount }
                      )
                    : intl.formatMessage(
                          { id: 'patientList.datasets.countFiltered' },
                          { shown: shownCount, count: totalCount }
                      )}
            </Typography>
        </Box>
    );
}
